import { css } from 'styled-components';
import { ButtonColorDefinition as ButtonVariantDefinition, IconPosition } from './ButtonVariant';
import { ButtonSizeDefinition, iconOnlyIconSize } from './ButtonSize';
import { Icon } from '../icon';
import { transition } from '../themes/mainTheme';

// Stroke thickens on press so a bordered button reads as engaged even where the
// border colour does not change. The extra 0.5px is an inset shadow, not a wider
// border — a wider border would grow the button by 1px and shift the layout.
/* eslint-disable no-magic-numbers */
const borderWidth = 1.5;
const pressedInset = 0.5;
/* eslint-enable no-magic-numbers */

export const basicButtonStyling = css`
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  transition: ${transition('background', 'border', 'color')};
  font-family: ${props => props.theme.font};
  position: relative;
  border-radius: 4px;
  line-height: 1;
  // Height is fixed per size now, so a wrapped label would overflow the box.
  white-space: nowrap;

  // Icons take the button's colour rather than carrying their own set of states.
  .icon {
    color: inherit;
  }

  .icon:before {
    vertical-align: baseline;
  }
`;

export const sizeDependantStyling = (
	sizeDefinition: ButtonSizeDefinition,
	hasLabel: boolean,
	hasIcon: boolean,
	iconPosition: IconPosition,
) => css`
  height: ${sizeDefinition.height}px;
  gap: ${sizeDefinition.gap}px;
  font-size: ${sizeDefinition.fontSize}px;
  font-weight: ${sizeDefinition.fontWeight};

  ${hasLabel ? css`
    padding-top: 0;
    padding-bottom: 0;
    padding-left: ${hasIcon && iconPosition === 'left'
		? sizeDefinition.paddingIcon
		: sizeDefinition.paddingHorizontal}px;
    padding-right: ${hasIcon && iconPosition === 'right'
		? sizeDefinition.paddingIcon
		: sizeDefinition.paddingHorizontal}px;

    & ${Icon.cssSelector} {
      font-size: ${sizeDefinition.iconSize}px;
    }
  ` : css`
    // Icon-only is a square of the button height with no padding, and the glyph
    // keeps its 24px at every size — at min the two meet and the icon fills the box.
    padding: 0;
    width: ${sizeDefinition.height}px;

    & ${Icon.cssSelector} {
      font-size: ${iconOnlyIconSize}px;
    }
  `}
`;

export const variantStyling = (
	variantDefinition: ButtonVariantDefinition,
	textButton: boolean,
	active?: boolean,
	$rounded?: boolean,
) =>
	css`
		${$rounded && css`
			border-radius: 30px;
		`}
      ${variantDefinition.border ? css`
        border-width: ${borderWidth}px;
        border-style: solid;
      ` : css`
        border: none;
      `}
      ${active ? css`
        color: ${variantDefinition.color.active};
        background: ${variantDefinition.background.active};
        ${variantDefinition.border && css`
          border-color: ${variantDefinition.border.active};
        `}
      ` : css`
        color: ${variantDefinition.color.default};
        background: ${variantDefinition.background.default};
        ${variantDefinition.border && css`
          border-color: ${variantDefinition.border.default};
        `}
      `}
      // The underline is permanent on a text button, and it owns no horizontal
      // padding — the label is the whole target.
      ${textButton && css`
        padding-left: 0;
        padding-right: 0;

        & span:not(.icon) {
          text-decoration: underline;
          text-underline-offset: 3px;
        }
      `}
      // Mouse-down fires :focus but not :focus-visible, so without this the
      // browser paints its own focus ring on press. Keyboard focus is unaffected —
      // the :focus-visible rule below still draws our ring.
      &:focus {
        outline: none;
      }

      // Ring is drawn with box-shadow, so keep a transparent outline for
      // Windows High Contrast Mode, where box-shadow is not rendered.
      &:focus-visible {
        outline: 2px solid transparent;
        outline-offset: 2px;
        box-shadow: ${props => variantDefinition.focusRing ?? props.theme.shadow.focus};
        color: ${variantDefinition.color.focus};
        background: ${variantDefinition.background.focus};
        ${variantDefinition.border && css`
          border-color: ${variantDefinition.border.focus};
        `}
      }

      &:hover,
      &.hover {
        color: ${variantDefinition.color.hover};
        background: ${variantDefinition.background.hover};
        ${variantDefinition.border && css`
          border-color: ${variantDefinition.border.hover};
        `}
      }

      // Disabled comes first so pressed can properly restyle
      &.disabled,
      &[disabled],
      &:disabled {
        color: ${variantDefinition.color.disabled};
        background: ${variantDefinition.background.disabled};
        ${variantDefinition.border && css`
          border-color: ${variantDefinition.border?.disabled};
        `}
      }

      &:not(:disabled):not(.disabled):active,
      &:not(:disabled):not(.disabled).pressed {
        color: ${variantDefinition.color.pressed};
        background: ${variantDefinition.background.pressed};
        ${variantDefinition.border && css`
          border-color: ${variantDefinition.border?.pressed};
          box-shadow: inset 0 0 0 ${pressedInset}px ${variantDefinition.border?.pressed};
        `}
      }
    `;
